import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { isEmpty } from 'lodash-es';
import {
  BaseBox,
  FlexBox,
  Heading3,
  Heading4,
  PrimaryButton,
  Icons,
} from '@docon/docon-ui-library';
import { getEnabledFeatures, getEnabledFeaturesForAll } from 'redux/reducer/app';
import { getDiscountBanner } from 'redux/reducer/prescription';
import { Image } from 'shared';
import { borderColor, colors } from 'utilities/colorConfig';
import { useHistory } from 'react-router';

const medImg = '/images/medBanner.svg';
const labImg = '/images/labBanner.svg';
const medLabImg = '/images/labAndMedBanner.svg';

const ImageBox = styled(BaseBox)`
  border-radius: 8px;
  padding-top: 10px;
  padding-left: 8px;
  width: 60px;
  height: 50px;
  margin-right: 7px;
  background-color: ${colors.blueColor[4]};
  
  ${Image} {
    height: 30px;
  }
`;

const Container = styled(BaseBox)`
  margin: 0px 4px 4px 4px;
  padding: 10px 11px;
  border-radius: 8px;
  background-color: ${colors.white};
  border: 1px solid ${borderColor};

  .cardSection {
    font-weight: 600;
    padding-bottom: 12px;
    align-items: center;
  }
`;

const Button = styled(PrimaryButton)`
  height: 32px;
  width: 100%;
  margin: 0;
  border-radius: 4px;
`;

const SmallBanner = styled(FlexBox)`
  padding: 9px 20px;
  background-color: ${colors.primaryBlue};
  justify-content: space-between;

  ${Heading4}{
    color: ${colors.white};
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
  }
`;

const DiscountBox = styled(FlexBox)`
  gap: 0.6rem;
  padding: 10px 5px;

  ${Heading3}{
    display: inline;
    font-weight: 600;
  }
  ${Heading4}{
    color: ${colors.secondaryColor};
  }
`;

const Root = styled(BaseBox)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;

  ${Icons.CrossIcon} {
    position: absolute;
    right: 20px;
    top: 20px;
    cursor: pointer;
  }
`;

function OrderMedicine({
  visitID,
  doctorID,
  patientID,
  source,
}: any) {
  const {
    feature,
    featureForAll,
  } = useSelector(state => state.app);
  const { bannerByType } = useSelector(state => state.prescription);
  const { user } = useSelector(state => state.auth);
  const [state, setState] = useState<any>({});
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    dispatch(getEnabledFeatures({ docID: doctorID, params: { visitIds: [visitID] } }));
    if (isEmpty(bannerByType)) {
      dispatch(getDiscountBanner({}));
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(feature) && feature.visitID === visitID) {
      if (isEmpty(featureForAll)) {
        dispatch(getEnabledFeaturesForAll(user.userID));
      } else {
        updateState();
      }
    }
  }, [feature]);

  useEffect(() => {
    if (!isEmpty(featureForAll)) {
      updateState();
    }
  }, [featureForAll]);

  function knowMore() {
    history.push(`/cart?visitID=${visitID}&patientID=${patientID}&doctorID=${doctorID}&utm_source=${source}`);
  }

  function updateState() {
    const {
      showMedicinesBanner,
      showDiagnosticsBanner,
      medicineWebBanner,
      labWebBanner,
      labAndMedWebBanner,
      cartLabWebBanner,
      cartMedicineWebBanner,
      diagnosticsCount,
      medicinesCount,
    }: any = { ...feature, ...bannerByType };
    if (feature.visitID !== visitID) return;
    const newState: any = {};
    const isMedicinePresentNOrdering = source === 'prescription' ? (showMedicinesBanner === 'whitelisted' || showMedicinesBanner === 'notPitched') && medicinesCount > 0 : showMedicinesBanner === 'whitelisted' && medicinesCount > 0;
    const isDiagnosticPresentNOrdering = source === 'prescription' ? (showDiagnosticsBanner === 'whitelisted' || showDiagnosticsBanner === 'notPitched') && diagnosticsCount > 0 : (showDiagnosticsBanner === 'whitelisted') && diagnosticsCount > 0;
    const showOfferCard = (isMedicinePresentNOrdering && medicineWebBanner != undefined && cartMedicineWebBanner !== undefined) || (isDiagnosticPresentNOrdering && labWebBanner !== undefined && cartLabWebBanner !== undefined);
    const isComboCard = isMedicinePresentNOrdering && isDiagnosticPresentNOrdering && labAndMedWebBanner != undefined;
    switch (true) {
      case isMedicinePresentNOrdering: {
        newState.title = medicineWebBanner?.title;
        newState.subTitle = medicineWebBanner?.subTitle;
        newState.cardTitleImg = medImg;
        newState.textPlaceOrderCTA = medicineWebBanner?.textPlaceOrderCTA;
        newState.background = colors.purpleColor[4];
      }
        break;
      case isDiagnosticPresentNOrdering: {
        newState.isLabTestOnly = true;
        newState.title = labWebBanner?.title;
        newState.subTitle = labWebBanner?.subTitle;
        newState.cardTitleImg = labImg;
        newState.textPlaceOrderCTA = labWebBanner?.textPlaceOrderCTA;
        newState.background = colors.blueColor[4];
      } break;
    }

    setState({
      ...newState, showOfferCard, isComboCard, diagnosticsCount, medicinesCount, isMedicinePresentNOrdering, showLabTestBanner: false, showTestCount: false,
    });
  }

  function handleSubmit() {
    history.push(`/cart?visitID=${visitID}&patientID=${patientID}&doctorID=${doctorID}&utm_source=${source}`);
  }

  function handleClose() {
    setState({
      ...state, showOfferCard: false, showTestCount: true,
    });
  }

  if (state.showTestCount)
    return (
      <Root>
        <SmallBanner>
          {
            state.medicinesCount > 0 && state.diagnosticsCount > 0 ?
              <Heading4
                title={`${state.medicinesCount} ${state.medicinesCount === 1 ? 'MEDICINE, ' : 'MEDICINES, '}${state.diagnosticsCount} ${state.diagnosticsCount === 1 ? 'TEST ' : 'TESTS '} INCLUDED`}
              /> :
              state.medicinesCount > 0 ?
                <Heading4 title={`${state.medicinesCount} ${state.medicinesCount === 1 ? 'MEDICINE INCLUDED' : 'MEDICINES INCLUDED'}`}
                /> :
                state.diagnosticsCount > 0 ?
                  <Heading4 title={`${state.diagnosticsCount} ${state.diagnosticsCount === 1 ? 'TEST INCLUDED' : 'TESTS INCLUDED'}`}
                  /> : null
          }
          <Heading4
            title={bannerByType.labAndMedWebBanner?.textPlaceOrderCTA}
            onClick={handleSubmit}
          />
        </SmallBanner>
      </Root>
    );

  if (!state.showOfferCard) return null;
  if (state.isComboCard)
    return (
      <Root>
        <Icons.CrossIcon onClick={handleClose} />
        <Container>
          <FlexBox className='cardSection'>
            <ImageBox>
              <Image src={medLabImg} />
            </ImageBox>
            <Heading3
              flex={1}
              title={bannerByType.labAndMedWebBanner?.title}
            />
          </FlexBox>
          <Button title={bannerByType.labAndMedWebBanner?.textPlaceOrderCTA} onClick={knowMore} />
        </Container>
      </Root>
    );

  return (
    <Root>
      <Icons.CrossIcon onClick={handleClose} />
      <Container hidden={state.isComboCard}>
        <DiscountBox>
          <ImageBox
            style={{
              'backgroundColor': `${state.background}`,
              'paddingLeft': '18px',
            }}
          >
            <Image src={state.cardTitleImg} />
          </ImageBox>
          <BaseBox flex={1}>
            <Heading3 title={state.title} />
            <Heading4 title={state.subTitle} />
          </BaseBox>
        </DiscountBox>

        <Button
          title={state.textPlaceOrderCTA}
          onClick={handleSubmit}
        />
      </Container>
    </Root>
  );
}

export default OrderMedicine;